import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import appConfig from '../config/appConfig';
import { setReferralCookie } from '../utils/referralUtils';

import ReactPlayer from 'react-player';
import Draggable from 'react-draggable';
import {
    Box,
    Paper,
    Dialog,
    Button,
    Snackbar,
    Alert,
    useMediaQuery,
    useTheme
} from '@mui/material';

// Components
import Navbar from '../components/landing/Navbar';
import HeroSection from '../components/landing/HeroSection';
import StatsBanner from '../components/landing/StatsBanner';
import AdExplainedSection from '../components/landing/AdExplainedSection';
import BenefitsSection from '../components/landing/BenefitsSection';
import AdvantagesSection from '../components/landing/AdvantagesSection';
import CTASection from '../components/landing/CTASection';
import HowItWorksSection from '../components/landing/HowItWorksSection';
import Ecosystem from '../components/landing/Ecosystem';

import Footer from '../components/landing/Footer';

function PaperComponent(props) {
    const nodeRef = useRef(null);
    return (
        <Draggable
            nodeRef={nodeRef}
            handle="#draggable-dialog-title"
            cancel={'[class*="MuiDialogContent-root"]'}
        >
            <Paper {...props} ref={nodeRef} />
        </Draggable>
    );
}

const LandingPage = () => {
    const theme = useTheme();
    const smMatched = useMediaQuery(theme.breakpoints.down('sm'));

    // UI State
    const [snackbarOpen, setSnackbarOpen] = useState(false);
    const [snackbarMessage, setSnackbarMessage] = useState('');
    const [snackbarSeverity, setSnackbarSeverity] = useState('success');

    // Video State
    const [openVideoTutorial, setOpenVideoTutorial] = useState(false);
    const [openVideoViewerTutorial, setOpenVideoViewerTutorial] = useState(false);

    // Wallet State
    const [isConnecting, setIsConnecting] = useState(false);
    const [isConnected, setIsConnected] = useState(false);
    const [account, setAccount] = useState(null);

    // Auth & Redirection Logic
    useEffect(() => {
        const checkAuthAndRedirect = async () => {
            try {
                const apiUrl = appConfig.API_URL;
                const response = await axios.get(`${apiUrl}/users/check-auth`, { withCredentials: true });

                if (response.data && response.data.id) {
                    const role = response.data.role;

                    if (role === 'User' || role === 'Viewer' || role === 'Admin-Viewer') {
                        window.location.href = `${appConfig.VIEWER_URL}/dashboard`;
                    } else if (role === 'Advertiser' || role === 'Admin-Advertiser') {
                        window.location.href = `${appConfig.ADVERTISER_URL}/dashboard`;
                    }
                    // Do not redirect Admin or Super Admin to ADMIN_URL automatically from the landing page
                }
            } catch (error) {
                // Not logged in or error, stay on landing page
                console.log('User not logged in or session expired');
            }
        };

        checkAuthAndRedirect();
    }, []);

    // Referral Capturing Logic & Redirection
    useEffect(() => {
        const searchParams = new URLSearchParams(window.location.search);
        const ref = searchParams.get('ref');
        const id = searchParams.get('id');

        if (ref) {
            // 1. Save referral code and ID in cookie
            setReferralCookie(ref, id);

            // 2. Track click and redirect
            const trackAndRedirect = async () => {
                try {
                    const apiUrl = appConfig.API_URL;
                    // We call the click tracking endpoint
                    await axios.post(`${apiUrl}/referrals/click`, { promoCode: ref, referrerId: id }, { withCredentials: true });
                } catch (error) {
                    console.error('Failed to track referral click:', error);
                } finally {
                    // 3. Redirect to login page, preserving the ref and id in the URL for the login page to handle
                    window.location.href = '/login' + window.location.search;
                }
            };

            trackAndRedirect();
        }
    }, []);

    useEffect(() => {
        if (typeof window.ethereum !== 'undefined' && window.ethereum.on) {
            window.ethereum.on('accountsChanged', (newAccounts) => {
                setAccount(newAccounts[0]);
                setIsConnected(newAccounts?.length > 0);
                if (newAccounts?.length > 0) {
                    localStorage.setItem('WalletAddress', newAccounts[0]);
                } else {
                    localStorage.removeItem('WalletAddress');
                }
            });
        }
    }, []);

    useEffect(() => {
        if (isConnected) {
            setSnackbarMessage('Connected to MetaMask');
            setSnackbarSeverity('success');
            setSnackbarOpen(true);
        }
    }, [isConnected]);

    // Initial check for wallet
    useEffect(() => {
        const checkConnection = async () => {
            const storedAddress = localStorage.getItem('WalletAddress');
            if (storedAddress) {
                if (window.ethereum) {
                    try {
                        const accounts = await window.ethereum.request({ method: 'eth_accounts' });
                        if (accounts.length > 0 && accounts[0].toLowerCase() === storedAddress.toLowerCase()) {
                            setAccount(accounts[0]);
                            setIsConnected(true);
                        } else {
                            localStorage.removeItem('WalletAddress');
                        }
                    } catch (err) {
                        console.error(err);
                    }
                }
            }
        };
        checkConnection();
    }, []);

    const handleConnect = async () => {
        setIsConnecting(true);
        if (!window.ethereum) {
            setSnackbarMessage('Please install MetaMask');
            setSnackbarSeverity('error');
            setSnackbarOpen(true);
            setIsConnecting(false);
            return;
        }

        try {
            const selectedAccount = await window.ethereum.request({
                method: 'eth_requestAccounts'
            });
            setAccount(selectedAccount[0]);
            localStorage.setItem('WalletAddress', selectedAccount[0]);
            setIsConnected(true);
        } catch (error) {
            setSnackbarMessage('Connection error');
            setSnackbarSeverity('error');
            setSnackbarOpen(true);
        } finally {
            setIsConnecting(false);
        }
    };

    const handleDisconnect = () => {
        localStorage.removeItem('WalletAddress');
        setIsConnected(false);
        setAccount(null);
        setSnackbarMessage('Wallet disconnected');
        setSnackbarSeverity('warning');
        setSnackbarOpen(true);
    };

    const handleClick = (buttonLabel) => {
        if (buttonLabel === 'Connect') {
            handleConnect();
        } else if (buttonLabel === 'Disconnect') {
            handleDisconnect();
        } else if (buttonLabel === 'connectAsViewer' || buttonLabel === 'connectAsAdvertiser') {
            window.location.href = '/login';
        } else if (buttonLabel === 'watchAdvVideo') {

        } else if (buttonLabel === 'watchAdvVideo') {
            setOpenVideoTutorial(true);
        } else if (buttonLabel === 'watchViewerVideo') {
            setOpenVideoViewerTutorial(true);
        }
    };

    const handleCloseSnackbar = () => setSnackbarOpen(false);
    const handleCloseTutorial = () => setOpenVideoTutorial(false);
    const handleCloseViewerTutorial = () => setOpenVideoViewerTutorial(false);

    return (
        <Box sx={{ minHeight: '100vh', overflowX: 'hidden' }}>
            <Navbar
                handleClick={handleClick}
                onConnect={handleConnect}
                isConnected={isConnected}
                account={account}
            />

            <HeroSection />

            <StatsBanner />

            <AdExplainedSection />



            <BenefitsSection />

            <AdvantagesSection />

            <Ecosystem />

            <HowItWorksSection />

            <CTASection />

            <Footer />

            {/* Dialogs */}
            <Dialog
                maxWidth="lg"
                open={openVideoViewerTutorial}
                onClose={handleCloseViewerTutorial}
                PaperComponent={PaperComponent}
                aria-labelledby="draggable-dialog-title"
                PaperProps={{
                    style: {
                        backgroundColor: 'rgba(24, 24, 24, 0.95)',
                        backdropFilter: 'blur(10px)',
                        border: '1px solid rgba(255,255,255,0.1)',
                        paddingBottom: '16px',
                        height: smMatched ? 'auto' : 'auto'
                    }
                }}
            >
                <ReactPlayer
                    url="https://vimeo.com/1040783575?share=copy"
                    controls={true}
                    width={smMatched ? '100%' : '800px'}
                    height={smMatched ? '200px' : '450px'}
                />
                <Button onClick={handleCloseViewerTutorial} sx={{ color: '#fff', mt: 2 }}>Close</Button>
            </Dialog>

            <Dialog
                maxWidth="lg"
                open={openVideoTutorial}
                onClose={handleCloseTutorial}
                PaperComponent={PaperComponent}
                aria-labelledby="draggable-dialog-title"
                PaperProps={{
                    style: {
                        backgroundColor: 'rgba(24, 24, 24, 0.95)',
                        backdropFilter: 'blur(10px)',
                        border: '1px solid rgba(255,255,255,0.1)',
                        paddingBottom: '16px',
                        height: smMatched ? 'auto' : 'auto'
                    }
                }}
            >
                <ReactPlayer
                    url="https://vimeo.com/1040785374?share=copy"
                    controls={true}
                    width={smMatched ? '100%' : '800px'}
                    height={smMatched ? '200px' : '450px'}
                />
                <Button onClick={handleCloseTutorial} sx={{ color: '#fff', mt: 2 }}>Close</Button>
            </Dialog>

            <Snackbar
                anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
                open={snackbarOpen}
                autoHideDuration={3000}
                onClose={handleCloseSnackbar}
            >
                <Alert onClose={handleCloseSnackbar} severity={snackbarSeverity} sx={{ width: '100%' }}>
                    {snackbarMessage}
                </Alert>
            </Snackbar>
        </Box>
    );
};

export default LandingPage;
